"use client";

import { cn } from "@/lib/utils";

/**
 * A completion bar.
 *
 * ```tsx
 * <Progress value={4} max={6} />
 * ```
 */
export function Progress({
  value,
  max,
  className,
  "aria-label": ariaLabel,
}: {
  value: number;
  max: number;
  className?: string;
  "aria-label"?: string;
}) {
  const fraction = max > 0 ? Math.min(1, Math.max(0, value / max)) : 0;
  // Clamped alongside the fill: a transient 7/6 from an optimistic update
  // should not announce an out-of-range value while the bar reads full.
  const announced = Math.min(Math.max(value, 0), max);
  return (
    <span
      role="progressbar"
      aria-valuenow={announced}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-label={ariaLabel}
      className={cn(
        "block h-1 w-full overflow-hidden rounded-nf-ds-pill bg-nf-ds-gray-200",
        className,
      )}
    >
      <span
        className="block h-full rounded-nf-ds-pill bg-nf-ds-purple-product transition-[width]"
        style={{ width: `${fraction * 100}%` }}
      />
    </span>
  );
}

/**
 * The same completion, drawn as one segment per item.
 *
 * Use where the total is small and countable and the items are things rather
 * than percentage — six checklist steps read as six, and a continuous bar
 * hides that the next one is a third of the work.
 *
 * ```tsx
 * <SegmentedProgress value={2} max={6} tone="done" />
 * ```
 */
export function SegmentedProgress({
  value,
  max,
  tone = "brand",
  className,
  "aria-label": ariaLabel,
}: {
  value: number;
  max: number;
  /** `done` for finished work, `brand` for progress toward a target. */
  tone?: "brand" | "done";
  className?: string;
  "aria-label"?: string;
}) {
  const announced = Math.min(Math.max(value, 0), Math.max(max, 0));
  return (
    <span
      role="progressbar"
      aria-valuenow={announced}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-label={ariaLabel}
      className={cn("flex w-full items-center gap-1", className)}
    >
      {Array.from({ length: Math.max(0, max) }, (_, index) => (
        <span
          key={`segment-${index}-of-${max}`}
          className={cn(
            "h-1 flex-1 rounded-nf-ds-pill transition-colors",
            index < value
              ? tone === "done"
                ? "bg-green-600"
                : "bg-nf-ds-purple-product"
              : "bg-nf-ds-gray-200",
          )}
        />
      ))}
    </span>
  );
}
